import { useCallback, useEffect, useMemo, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { Alert } from '@vtex/shoreline'
import { t } from 'i18next'

import {
  Drawer,
  ToggleButton,
  useColumnVisibility
} from '~/components/ColumnSelector'
import HeaderPages from '~/components/HeaderPages'
import ListPagination from '~/components/ListPagination'
import Loading from '~/components/Loading'
import LoadingPopUp from '~/components/LoadingPopUp'
import Table from '~/components/Table'
import {
  formatDateToString,
  formatSearchString,
  getAccountId,
  history
} from '~/helpers'
import { handleSortDirection } from '~/helpers/sortData'
import { useErrorApiAlert } from '~/hooks/useErrorApiAlert'
import useQueryParams from '~/hooks/useQuery'
import ExportButton from '~/modules/retailMedia/components/ExportButton'
import { formatGroceryListAdvertisers } from '~/modules/retailMedia/dtos/grocery/advertiser/list'
import { getNewMetricsAvailability } from '~/modules/retailMedia/helpers/newMetricsAvailability'
import { useClickMetricsCutoffAlert } from '~/modules/retailMedia/hooks/useClickMetricsCutoffAlert'
import { useImpressionShareEnabled } from '~/modules/retailMedia/hooks/useImpressionShareEnabled'
import {
  getAdvertiserReportKey,
  getAdvertiserReportService
} from '~/modules/retailMedia/services/report'
import { useAppSelector } from '~/store/hooks'

import AdvertisersFilters from '../AdvertiserFilters'
import { schema } from './schema'

const ADVERTISERS_TABLE_READONLY_KEYS = [
  'title',
  'balance'
] as const satisfies ReadonlyArray<keyof ReturnType<typeof schema>>

const AdvertisersTable = () => {
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false)
  const publisherId = getAccountId()
  const impressionShareEnabled = useImpressionShareEnabled({ publisherId })
  const searchParams = useQueryParams()
  const page = Number(searchParams.get('page')) || 1
  const tag_id = searchParams.get('tag_id') as PublisherTagId
  const quantity = Number(searchParams.get('quantity')) || 50
  const advertiser_name = searchParams.get('advertiser_name')
  const seller_id = searchParams.get('seller_id')
  const sort_direction = searchParams.get('sort_direction') as SortDirection
  const sort_key = searchParams.get('sort_key')

  const {
    datepickerHeader: { startDate, endDate }
  } = useAppSelector(state => state)

  const params = useMemo(
    () => ({
      page,
      start_date: formatDateToString(startDate, 'YYYY-MM-DD'),
      end_date: formatDateToString(endDate, 'YYYY-MM-DD'),
      advertiser_name,
      tag_id,
      seller_id,
      account_info: true,
      quantity,
      order_direction: sort_direction,
      order_by: sort_key,
      count: true
    }),
    [
      page,
      startDate,
      endDate,
      advertiser_name,
      tag_id,
      seller_id,
      quantity,
      sort_direction,
      sort_key
    ]
  )

  const {
    isPending,
    isFetching,
    data: { data, total } = {},
    error
  } = useQuery({
    refetchInterval: 60000,
    queryKey: [getAdvertiserReportKey, params],
    queryFn: async () => {
      const res = await getAdvertiserReportService(params)
      const { data } = res
      const formatted = formatGroceryListAdvertisers(data.data)

      return { ...data, data: formatted }
    }
  })

  const [AlertOnError, setError] = useErrorApiAlert({
    title: t('c:messages.errorOccurred.title')
  })

  const availableMetrics = useMemo(() => {
    let metrics = getNewMetricsAvailability(
      data?.map(item => item?.metrics?.raw || null) || []
    )
    if (publisherId && !impressionShareEnabled) {
      metrics = {
        ...metrics,
        impressionShare: false,
        lostIsRank: false,
        lostIsBudget: false
      }
    }
    // NTB agregado entre campanhas conta o mesmo comprador 2+ vezes; colunas ocultas por ora
    return {
      ...metrics,
      newToBrandCustomers: false,
      newToBrandRevenue: false
    }
  }, [data, impressionShareEnabled, publisherId])

  const handleOpenColumnSelector = useCallback(() => {
    setIsColumnSelectorOpen(true)
  }, [])

  const handleCloseColumnSelector = useCallback(() => {
    setIsColumnSelectorOpen(false)
  }, [])

  const showClickAlert = useClickMetricsCutoffAlert()

  const schemaWithMetrics = useMemo(
    () => schema({ availableMetrics, showClickAlert }),
    [availableMetrics, showClickAlert]
  )

  const { columns, visibleColumnsSchema, onColumnsChange, onResetToDefault } =
    useColumnVisibility({
      schema: schemaWithMetrics,
      tableName: 'advertisers',
      readonlyKeys: ADVERTISERS_TABLE_READONLY_KEYS
    })

  useEffect(() => {
    if (error) setError(error)
  }, [error, setError])

  /**
   * Handle Pagination
   */
  const handlePagination = useCallback(
    (value: number) => {
      searchParams.set('page', String(value))
      history.push({ search: searchParams.toString() })
    },
    [searchParams]
  )

  const handleItemsPerPageChange = useCallback(
    (value: { value: number }) => {
      searchParams.set('quantity', String(value.value))
      searchParams.set('page', '1')
      history.push({ search: searchParams.toString() })
    },
    [searchParams]
  )
  /**
   * Sort list
   */
  const handleSortList = useCallback(
    (data: OnSortProps) => {
      const selectedKey = data.key
      const sortInitialDirection = data?.sortInitialDirection

      const response = handleSortDirection({
        selectedKey,
        currentSortDirection: sort_direction,
        currentSortKey: sort_key,
        sortInitialDirection,
        allowReset: true
      })

      if (!response.sortDirection) {
        searchParams.delete('sort_direction')
        searchParams.delete('sort_key')
      }

      if (response.sortDirection) {
        searchParams.set('sort_direction', response.sortDirection)
        searchParams.set('sort_key', response.sortKey)
      }

      history.push({ search: searchParams.toString() })
    },
    [searchParams, sort_direction, sort_key]
  )

  return (
    <>
      <HeaderPages
        title={t('rm:AdvertisersList')}
        sideRightComponent={
          <>
            <ToggleButton onClick={handleOpenColumnSelector} />
            <ExportButton
              queryParams={formatSearchString(params)}
              exportType={'advertisers'}
            />
          </>
        }
      />
      <Drawer
        columns={columns}
        isOpen={isColumnSelectorOpen}
        onClose={handleCloseColumnSelector}
        onColumnsChange={onColumnsChange}
        onResetToDefault={onResetToDefault}
      />

      <section>
        <AdvertisersFilters />
      </section>

      <Loading status={isPending}>{t('c:actions.loadingData')}...</Loading>

      <LoadingPopUp isActive={isFetching} />

      <AlertOnError />

      <Loading status={!isPending && isFetching} onlyLine />

      {!!data && (
        <Table
          schema={visibleColumnsSchema}
          freeze="first-row-and-column"
          data={data}
          total={total}
          sideBorder
          smallerRowPadding
          // sort settings
          activeSortDirection={sort_direction}
          activeSortKey={sort_key}
          onSort={handleSortList}
        />
      )}

      {total === 0 && data && (
        <Alert variant="warning">{t('c:table.emptyFiltered')}</Alert>
      )}

      <ListPagination
        total={total}
        label={t(
          `rm:advertisers.pagination.label.${total > 1 ? 'plural' : 'singular'}`
        )}
        currentPage={page}
        itemsPerPage={quantity}
        menuPlacement="top"
        onClickPagination={handlePagination}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
    </>
  )
}

export default AdvertisersTable
